import React, { memo, useEffect, useMemo } from 'react';

import { orderBy } from 'lodash';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useQueryGetTagsAssignedToCurrentCustomerSubPrograms } from 'api/queries/getTagsAssignedToCurrentCustomerSubPrograms.query';

import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { Select } from 'components/DesignSystem/Select';

type ProgramTagOption = {
    label: string;
    value: string;
};

interface IProps {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    value?: string[];
}

export const ProgramTagsFilter = memo(({ methods, name, filtersStatusRef, handleFilters, value }: IProps) => {
    const { data, isSuccess } = useQueryGetTagsAssignedToCurrentCustomerSubPrograms();

    const options = useMemo<ProgramTagOption[]>(
        () =>
            data
                ? orderBy(
                      data.map((item) => ({ label: item.name, value: item.name })),
                      [(item: any) => item['label'].toLowerCase()],
                      ['asc'],
                  )
                : [],
        [data],
    );

    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
            return;
        }

        if (value && value.length > 0 && data) {
            methods?.setValue(name, value);
        }
        signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
    }, [value, data, filtersStatusRef, name, methods, isSuccess]);

    const handleChange = (selected: readonly ProgramTagOption[]) => {
        methods?.setValue(
            name,
            selected.map((item) => item.value),
        );
        handleFilters?.();
    };

    const handleValue = (formValue?: string[]): ProgramTagOption[] => {
        if (!formValue?.length) return [];
        const selected = new Set(formValue);
        return options.filter((opt) => selected.has(opt.value));
    };

    return (
        <div className={classes.selectWrapper}>
            <div data-testid={'program-tags-filter'}>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<string>, true>
                            {...field}
                            options={options}
                            isMulti
                            onChange={handleChange}
                            value={handleValue(field.value)}
                            placeholder={'Select Tags...'}
                            label={FilterLabels.TAG_PORTFOLIO_BOARD}
                        />
                    )}
                    name={name}
                    control={methods?.control}
                />
            </div>
        </div>
    );
});
